import { Cell, Grid, Modal, ModalBody, ModalHeader, Tabs } from 'bold-ui'
import { PageLoading } from 'components/loading'
import { TabLink } from 'components/route'
import { useFlags } from 'config/useFlagsContext'
import React from 'react'
import { useHistory, useRouteMatch } from 'react-router'
import { MetaArray } from 'util/metaPath'
import { CidadaoMedicoes } from 'view/atendimentos/detail/soap/aside/medicoes/model'
import { TipoPreNatal } from 'view/atendimentos/detail/soap/pre-natal/model-preNatal'
import { HistoricoMedicaoModel } from 'view/atendimentos/types/model-historicoMedicao'

import { MedicoesPreNatalAtendimentoModel } from '../types/model-historicoPreNatal'
import { PeriodoGestacaoModel } from '../types/PeriodoGestacaoModel'
import { EditableHistoricoMedicoesModalBody } from './EditableHistoricoMedicoesModalBody'
import { MODAL_MEDICAO_ANTERIOR_EXAME_FISICO_TAB_URL } from './exame-fisico/model-medicaoAnteriorExameFisico'
import { MedicaoAnteriorModel } from './model-medicoes'
import { ReadOnlyHistoricoMedicoesModalBody } from './ReadOnlyHistoricoMedicoesModalBody'

export interface HistoricoMedicoesModalProps {
  cidadao: CidadaoMedicoes
  prontuarioId: ID
  medicoes: ReadonlyArray<HistoricoMedicaoModel>
  loadingMedicoes: boolean
  dataAtendimento?: Instant
  isGestante: boolean
  gestacoes: ReadonlyArray<PeriodoGestacaoModel>
  isAtendimentoObservacao: boolean
  isAtendimentoOdonto: boolean
  isAtendimentoProcedimentos: boolean
  tipoPreNatal?: TipoPreNatal
  isRetificacao?: boolean
  editable?: false
}

export interface EditableHistoricoMedicoesModalProps
  extends Omit<HistoricoMedicoesModalProps, 'editable'>,
    MedicoesPreNatalAtendimentoModel {
  fieldName: MetaArray<MedicaoAnteriorModel>
  editable: true
}

export function HistoricoMedicoesModal(props: HistoricoMedicoesModalProps | EditableHistoricoMedicoesModalProps) {
  const {
    cidadao,
    prontuarioId,
    medicoes = [],
    loadingMedicoes,
    isGestante,
    gestacoes,
    isAtendimentoObservacao,
    tipoPreNatal,
    dataAtendimento,
    isRetificacao = false,
  } = props

  const history = useHistory()
  const match = useRouteMatch()
  const { PAC_EQUIPAMENTO_ENABLED } = useFlags()

  const onClose = () => {
    return history.push(match.url.replace('/historicoMedicoes', ''))
  }

  const initialRoute = !isGestante || isRetificacao ? '/antropometria' : '/pre-natal'
  const showGestacaoTab = !isRetificacao && isGestante

  return (
    <Modal open onClose={onClose}>
      <ModalHeader title='Histórico de medições' />
      <ModalBody>
        <Grid alignItems='flex-start' gap={0}>
          <Cell size={12}>
            <Tabs>
              {showGestacaoTab && <TabLink to={`${match.url}/pre-natal`}>Gestação</TabLink>}
              <TabLink data-testid='HistoricoMedicoesModal.tabAntropometria' to={`${match.url}/antropometria`}>
                Antropometria
              </TabLink>
              {!isRetificacao && (
                <>
                  <TabLink to={`${match.url}/sinaisVitais`}>Sinais vitais</TabLink>
                  <TabLink to={`${match.url}/glicemia`}>Glicemia</TabLink>
                  {PAC_EQUIPAMENTO_ENABLED && (
                    <TabLink to={`${match.url}${MODAL_MEDICAO_ANTERIOR_EXAME_FISICO_TAB_URL}`}>Exame físico</TabLink>
                  )}
                </>
              )}
            </Tabs>
          </Cell>
          <Cell size={12}>
            {loadingMedicoes ? (
              <PageLoading message='Carregando dados' />
            ) : props.editable ? (
              <EditableHistoricoMedicoesModalBody
                initialRoute={initialRoute}
                match={match}
                medicoes={medicoes}
                isAtendimentoObservacao={isAtendimentoObservacao}
                tipoPreNatal={tipoPreNatal}
                isGestante={isGestante}
                isRetificacao={isRetificacao}
                {...props}
              />
            ) : (
              <ReadOnlyHistoricoMedicoesModalBody
                initialRoute={initialRoute}
                match={match}
                cidadao={cidadao}
                prontuarioId={prontuarioId}
                medicoes={medicoes}
                gestacoes={gestacoes}
                isAtendimentoObservacao={isAtendimentoObservacao}
                isGestante={isGestante}
                dataAtendimento={dataAtendimento}
              />
            )}
          </Cell>
        </Grid>
      </ModalBody>
    </Modal>
  )
}
